import React, { useState, useEffect, useRef, useCallback } from 'react';
import Plot from 'react-plotly.js';
import './App.css';
import { IoMicOutline } from "react-icons/io5";
import { CgAttachment } from 'react-icons/cg';
import { MdSend } from 'react-icons/md';
import { FaFile } from 'react-icons/fa'; 
import assistantIcon from './assets/assistant_iconv2.png';
// import logo from './assets/logo.png';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { v4 as uuidv4 } from 'uuid';
import TableRenderer from './components/TableRenderer';
import CodeBlock from './components/CodeBlock';
import Sidebar from './components/Sidebar';
import GuidedRun from './components/GuidedRun';
import { downloadChatAsHTML } from './utils/chatDownloader';

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8000`;

function App() {
  const [input, setInput] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      type: 'bot',
      message: `👋 **Hi there! Welcome to the TLF Assistant.**

An AI-powered assistant for generating TLFs (Tables, Listings, Figures) for clinical trials.

Leverages built-in CDISC Pilot datasets (ADSL, ADTTE, ADADAS, ADLBC, ...) and SAP shell extraction tools to create validated outputs.

💡 **Start asking me to generate a summary table, KM plot, ANCOVA analysis, or extract a mock shell — I'm ready!**`
    }
  ]);

  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedFile, setSelectedFile] = useState(null);
  const [theme, setTheme] = useState('dark'); 
  const [showGuidedRun, setShowGuidedRun] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [sessionId, setSessionId] = useState(() => {
    const existingSessionId = sessionStorage.getItem('session_id');
    if (existingSessionId) {
      fetch(`${BACKEND_BASE}/api/cleanup?old_session_id=${existingSessionId}`, {
        method: 'GET',
      });
    }
    const newSessionId = uuidv4();
    sessionStorage.setItem('session_id', newSessionId);
    return newSessionId;
  });
  
  const [suggestionBubbles, setSuggestionBubbles] = useState([
    { text: "Generate a table for Primary Endpoint Analysis: ADAS Cog (11) – Change from Baseline to Week 24 – LOCF" },
    { text: "Generate a KM plot for the Time to Dermatologic Event: Safety population by Treatment Group" },
    { text: "Create a table for Summary of Demographic and Baseline Characteristics" }
  ]);

  const lastMessageRef = useRef(null);
  const recognitionRef = useRef(null);
  const socketRef = useRef(null);
  const wsInitRef = useRef(false);

  // Safely send over WebSocket: waits for OPEN if still CONNECTING
  const safeWSSend = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket) return;
    const data = typeof payload === 'string' ? payload : JSON.stringify(payload);
    if (socket.readyState === WebSocket.OPEN) {
      socket.send(data);
    } else if (socket.readyState === WebSocket.CONNECTING) {
      const onOpen = () => {
        try { socket.send(data); } catch (_) {}
        socket.removeEventListener('open', onOpen);
      };
      socket.addEventListener('open', onOpen, { once: true });
    }
  }, []);

  // Function to detect and extract tables from markdown content
  const extractTablesFromMarkdown = (content) => {
    if (!content) return { content, tables: [] };
    
    const lines = content.split('\n');
    const tables = [];
    let currentTable = [];
    let inTable = false;
    let tableStartIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check if line looks like a table row (contains | and starts with |)
      if (line.includes('|') && line.trim().startsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableStartIndex = i;
          currentTable = [line];
        } else {
          currentTable.push(line);
        }
      } else if (inTable) {
        // End of table detected
        if (currentTable.length >= 2) { // At least header and separator
          const tableContent = currentTable.join('\n');
          const tableId = `table-${tables.length}`;
          tables.push({
            id: tableId,
            content: tableContent,
            startIndex: tableStartIndex,
            endIndex: i - 1
          });
        }
        inTable = false;
        currentTable = [];
        tableStartIndex = -1;
      }
    }
    
    // Handle table at end of content
    if (inTable && currentTable.length >= 2) {
      const tableContent = currentTable.join('\n');
      const tableId = `table-${tables.length}`;
      tables.push({
        id: tableId,
        content: tableContent,
        startIndex: tableStartIndex,
        endIndex: lines.length - 1
      });
    }
    
    // Replace tables in content with placeholders (reverse order to keep indices stable)
    let processedContent = content;
    const tablesWithIndex = tables.map((table, index) => ({ table, index }));
    tablesWithIndex.sort((a, b) => b.table.startIndex - a.table.startIndex);
    tablesWithIndex.forEach(({ table, index }) => {
      const placeholder = `\n\n[TABLE_PLACEHOLDER_${index}]\n\n`;
      const linesToReplace = table.endIndex - table.startIndex + 1;
      const linesArray = processedContent.split('\n');
      linesArray.splice(table.startIndex, linesToReplace, placeholder);
      processedContent = linesArray.join('\n');
    });
    
    return { content: processedContent, tables };
  };

  // Function to render message content with table and code support
  const renderMessageContent = (message) => {
    if (!message) return null;
    
    const { content, tables } = extractTablesFromMarkdown(message);
    
    return (
      <div className="message-content">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]} 
          rehypePlugins={[rehypeRaw]}
          components={{
            // Custom renderer for table placeholders
            p: ({ children }) => {
              const text = children?.toString() || '';
              if (text.includes('[TABLE_PLACEHOLDER_')) {
                const match = text.match(/\[TABLE_PLACEHOLDER_(\d+)\]/);
                if (match) {
                  const tableIndex = parseInt(match[1]);
                  const table = tables[tableIndex];
                  if (table) {
                    return <TableRenderer tableData={table.content} title={`Table ${tableIndex + 1}`} />;
                  }
                }
              }
              return <p>{children}</p>;
            },
            // Custom renderer for code blocks
            code: ({ children, className, inline }) => {
              const language = className ? className.replace('language-', '') : '';
              return (
                <CodeBlock 
                  className={className}
                  language={language}
                  inline={inline}
                >
                  {children}
                </CodeBlock>
              );
            },
            // Custom renderer for pre blocks (code blocks)
            pre: ({ children }) => {
              const codeElement = React.Children.only(children);
              const { className, children: codeChildren } = codeElement.props;
              const language = className ? className.replace('language-', '') : '';
              
              return (
                <CodeBlock 
                  className={className}
                  language={language}
                  inline={false}
                >
                  {codeChildren}
                </CodeBlock>
              );
            }
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    if (wsInitRef.current) return; // prevent double-init in React StrictMode
    wsInitRef.current = true;

    const wsUrl = `${BACKEND_BASE.replace(/^http/, 'ws')}/ws`;

    const connect = () => {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('Connected to server via WebSocket');
      };

      socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Received message:', data);

      if (data.type === 'ping') {
        safeWSSend({ type: 'pong' });
        return;
      }

      if (data.type === 'bot' && (data.message || data.graph || data.file)) {
        setChatHistory((prevHistory) => [
          ...prevHistory,
          {
            type: 'bot',
            message: data.message || null,
            graph: data.graph || null,
            file: data.file || null,
          },
        ]);
        setLoading(false);
      } else if (data.type === 'thinking') {
        // Thinking messages removed as requested
        console.log('Thinking message received but ignored:', data.message);
      } else if (data.type === 'ai_response') {
        // Handle AI response messages
        if (data.replace_thinking) {
          // Replace thinking with AI response
          setChatHistory(prev => {
            const filtered = prev.filter(chat => chat.type !== 'thinking');
            return [...filtered, {
              type: 'bot',
              message: data.message,
              timestamp: new Date().toISOString()
            }];
          });
        } else {
          // Add new AI response
          setChatHistory(prev => [...prev, {
            type: 'bot',
            message: data.message,
            timestamp: new Date().toISOString()
          }]);
        }
      } else if (data.type === 'integrated_thinking') {
        // Handle integrated thinking messages with tool operations
        setChatHistory(prev => [...prev, {
          type: 'integrated_thinking',
          message: data.message,
          tool_operations: data.tool_operations,
          timestamp: new Date().toISOString()
        }]);
      } else if (data.type === 'tool_working') {
        // Handle tool working messages
        if (data.replace_thinking) {
          // Replace thinking with tool working
          setChatHistory(prev => {
            const filtered = prev.filter(chat => chat.type !== 'thinking');
            return [...filtered, {
              type: 'tool_working',
              message: data.message,
              tool_name: data.tool_name,
              tool_input: data.tool_input,
              timestamp: new Date().toISOString()
            }];
          });
        } else {
          // Add new tool working message
          setChatHistory(prev => [...prev, {
            type: 'tool_working',
            message: data.message,
            tool_name: data.tool_name,
            tool_input: data.tool_input,
            timestamp: new Date().toISOString()
          }]);
        }
      } else if (data.type === 'tool_integrated') {
        // Handle integrated tool messages (calling + getting results in same box)
        // Update existing tool message or add new one
        setChatHistory(prev => {
          const filtered = prev.filter(chat => chat.type !== 'tool_integrated' || chat.tool_name !== data.tool_name);
          return [...filtered, {
            type: 'tool_integrated',
            message: data.message,
            tool_name: data.tool_name,
            tool_input: data.tool_input,
            tool_output: data.tool_output,
            status: data.status,
            timestamp: new Date().toISOString()
          }];
        });
      } else if (data.type === 'tool_progress') {
        // Handle tool progress messages (like Streamlit)
        if (data.replace_thinking) {
          // Replace thinking with tool progress
          setChatHistory(prev => {
            const filtered = prev.filter(chat => chat.type !== 'thinking');
            return [...filtered, {
              type: 'tool_progress',
              message: data.message,
              tool_name: data.tool_name,
              tool_input: data.tool_input,
              tool_output: data.tool_output,
              timestamp: new Date().toISOString()
            }];
          });
        } else {
          // Add new tool progress message
          setChatHistory(prev => [...prev, {
            type: 'tool_progress',
            message: data.message,
            tool_name: data.tool_name,
            tool_input: data.tool_input,
            tool_output: data.tool_output,
            timestamp: new Date().toISOString()
          }]);
        }
      } else if (data.type === 'suggestions' && data.suggestions) {
        const newSuggestions = data.suggestions.map((text) => ({ text }));
        setSuggestionBubbles(newSuggestions);
      } else if (data.type === 'error') {
        console.error('Error received from server:', data.message);
        setLoading(false);
      }
      };

      socket.onclose = () => {
        console.log('Disconnected from server');
        // Optional: simple retry after delay in dev to handle hot reload
        setTimeout(() => {
          if (wsInitRef.current) connect();
        }, 1500);
      };
    };

    connect();

    return () => {
      wsInitRef.current = false;
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }
    };
  }, []);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSubmit = useCallback(() => {
    if (!input.trim() || loading) return;
  
    setChatHistory((prevHistory) => [
      ...prevHistory,
      { type: 'user', message: input },
    ]);
  
    const userInput = input;
    setInput('');
    setLoading(true);
  
    safeWSSend({
      type: 'user',
      message: userInput,
      session_id: sessionId,
    });
  }, [input, setChatHistory, setInput, setLoading, sessionId, socketRef, loading]);
  

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !loading) {
        event.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [input, handleSubmit, loading]); 

  const handleMicClick = () => {
    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      startVoiceRecognition();
    }
  };

  const startVoiceRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsRecording(false);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.start();
    } else {
      alert('Your browser does not support voice recognition. Please use Chrome or another supported browser.');
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setChatHistory((prevHistory) => [
        ...prevHistory,
        { type: 'user', file: { name: file.name } },
      ]);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId); 

      try {
        const res = await fetch(`${BACKEND_BASE}/api/upload`, {
          method: 'POST',
          body: formData,
        });

        if (res.ok) {
          setChatHistory((prevHistory) => [
            ...prevHistory,
            { type: 'bot', message: `"${file.name}" uploaded successfully. How can I assist with it?` },
          ]);
        } else {
          console.error('File upload failed:', res.statusText);
        }
      } catch (error) {
        console.error('File upload error:', error);
        setChatHistory((prevHistory) => [
          ...prevHistory,
          { type: 'bot', message: 'An error occurred while uploading the file. Please try again.' },
        ]);
      }
    }
  };

  const handleSuggestionClick = (suggestionText) => {
    if (loading) return;
    
    setChatHistory((prevHistory) => [
      ...prevHistory,
      { type: 'user', message: suggestionText },
    ]);
    setLoading(true);
  
    safeWSSend({
      type: 'user',
      message: suggestionText,
      session_id: sessionId,
    });
  };

  // Sidebar handlers
  const handleExampleClick = (prompt) => {
    setInput(prompt);
  };

  const handleHistoryClick = (question) => {
    setInput(question);
  };

  const handleDownloadChat = () => {
    downloadChatAsHTML(chatHistory, theme);
  };

  const handleGuidedRunClose = () => {
    setShowGuidedRun(false);
  };

  const handleGuidedRunMessage = (message) => {
    setShowGuidedRun(false);
    setInput(message);
    // Trigger the message send
    setTimeout(() => {
      handleSubmit();
    }, 100);
  };
  
  return (
    <div className="app-container">
      <Sidebar 
        chatHistory={chatHistory}
        onExampleClick={handleExampleClick}
        onHistoryClick={handleHistoryClick}
        onDownloadChat={handleDownloadChat}
      />
      
      <header className="site-header">
        <div className="header-text">
          <h2>🚀 TLF Assistant</h2>
          <p className="header-subtitle">Generate clinical trial tables, listings, and figures, and built-in CDISC datasets.</p>
        </div>
        <div className="header-actions">
          <div className="theme-toggle" onClick={toggleTheme}>
            <div className={`theme-toggle-switch ${theme}`} />
          </div>
          <button 
            className="guided-run-btn"
            onClick={() => setShowGuidedRun(true)}
          >
            🎯 Guided Run
          </button>
        </div>
      </header>

      <div className="chat-container">
        <div className="chat-window">
          {chatHistory.map((chat, index) => (
            <div
              key={index}
              className={`chat-message ${chat.type === 'user' ? 'user-message' : 'assistant-message'}`}
              ref={index === chatHistory.length - 1 ? lastMessageRef : null}
            >
              {chat.type === 'thinking' ? (
                <div className="thinking-container">
                  <div className="thinking-icon">🧠</div>
                  <div className="thinking-text">
                    <div className="thinking-message">{chat.message}</div>
                    <div className="thinking-status" style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                      Processing your request...
                    </div>
                  </div>
                </div>
              ) : chat.type === 'integrated_thinking' ? (
                <div className="integrated-thinking-container">
                  <div className="thinking-icon">🧠</div>
                  <div className="thinking-text">
                    <div className="thinking-message">{chat.message}</div>
                    {chat.tool_operations && chat.tool_operations.length > 0 && (
                      <details className="tool-operations-details">
                        <summary style={{ cursor: 'pointer', fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
                          View tool operations ({chat.tool_operations.length})
                        </summary>
                        <div className="tool-operations-list">
                          {chat.tool_operations.map((tool, index) => (
                            <div key={index} className="tool-operation-item">
                              <div className="tool-operation-header">
                                <span className="tool-working-icon">🛠️</span>
                                <span className="tool-working-text">Working with `{tool.tool_name}`</span>
                                <span className="tool-progress-icon">🔧</span>
                                <span className="tool-progress-text">Getting results from `{tool.tool_name}`</span>
                              </div>
                              <details className="tool-operation-details">
                                <summary style={{ cursor: 'pointer', fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                                  View details
                                </summary>
                                {tool.tool_input && (
                                  <div style={{ marginTop: '8px' }}>
                                    <strong style={{ fontSize: '0.8em', color: '#1976d2' }}>Input:</strong>
                                    <pre style={{ 
                                      fontSize: '0.75em', 
                                      backgroundColor: '#e3f2fd', 
                                      padding: '6px', 
                                      borderRadius: '4px',
                                      marginTop: '4px',
                                      overflow: 'auto',
                                      maxHeight: '120px'
                                    }}>
                                      {tool.tool_input}
                                    </pre>
                                  </div>
                                )}
                                {tool.tool_output && (
                                  <div style={{ marginTop: '8px' }}>
                                    <strong style={{ fontSize: '0.8em', color: '#388e3c' }}>Output:</strong>
                                    <pre style={{ 
                                      fontSize: '0.75em', 
                                      backgroundColor: '#e8f5e8', 
                                      padding: '6px', 
                                      borderRadius: '4px',
                                      marginTop: '4px',
                                      overflow: 'auto',
                                      maxHeight: '120px'
                                    }}>
                                      {tool.tool_output}
                                    </pre>
                                  </div>
                                )}
                              </details>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              ) : chat.type === 'tool_integrated' ? (
                <div className="tool-integrated-container">
                  <div className="tool-integrated-icon">🛠️</div>
                  <div className="tool-integrated-text">
                    <div className="tool-integrated-message">Working with tools</div>
                    <div className="tool-status">
                      {chat.status === 'calling' ? 'Executing tool...' : 'Tool completed successfully ✅'}
                    </div>
                    <details className="tool-details" open={false}>
                      <summary className="tool-details-summary">
                        {chat.tool_name ? `Tool: ${chat.tool_name}` : 'View tool details'}
                      </summary>
                      {chat.tool_input && (
                        <div className="tool-input-content">
                          <strong className="tool-input-label">Input:</strong>
                          <pre className="tool-input-pre">
                            {chat.tool_input}
                          </pre>
                        </div>
                      )}
                      {chat.tool_output && (
                        <div className="tool-output-content">
                          <strong className="tool-output-label">Output:</strong>
                          <pre className="tool-output-pre">
                            {chat.tool_output}
                          </pre>
                        </div>
                      )}
                    </details>
                  </div>
                </div>
              ) : chat.type === 'tool_working' ? (
                <div className="tool-working-container">
                  <div className="tool-working-icon">🛠️</div>
                  <div className="tool-working-text">
                    <div className="tool-working-message">{chat.message}</div>
                    <div className="tool-status" style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                      Executing tool...
                    </div>
                    {chat.tool_input && (
                      <details className="tool-input-details">
                        <summary style={{ cursor: 'pointer', fontSize: '0.9em', color: '#666' }}>
                          View tool input
                        </summary>
                        <pre style={{ 
                          fontSize: '0.8em', 
                          backgroundColor: '#e3f2fd', 
                          padding: '10px', 
                          borderRadius: '5px',
                          marginTop: '5px',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          {chat.tool_input}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ) : chat.type === 'tool_progress' ? (
                <div className="tool-progress-container">
                  <div className="tool-progress-icon">🔧</div>
                  <div className="tool-progress-text">
                    <div className="tool-progress-message">{chat.message}</div>
                    <div className="tool-status" style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                      Tool completed successfully ✅
                    </div>
                    {(chat.tool_input || chat.tool_output) && (
                      <details className="tool-details">
                        <summary style={{ cursor: 'pointer', fontSize: '0.9em', color: '#666' }}>
                          View tool details
                        </summary>
                        {chat.tool_input && (
                          <div style={{ marginTop: '10px' }}>
                            <strong style={{ fontSize: '0.85em', color: '#1976d2' }}>Input:</strong>
                            <pre style={{ 
                              fontSize: '0.8em', 
                              backgroundColor: '#e3f2fd', 
                              padding: '8px', 
                              borderRadius: '4px',
                              marginTop: '5px',
                              overflow: 'auto',
                              maxHeight: '150px'
                            }}>
                              {chat.tool_input}
                            </pre>
                          </div>
                        )}
                        {chat.tool_output && (
                          <div style={{ marginTop: '10px' }}>
                            <strong style={{ fontSize: '0.85em', color: '#388e3c' }}>Output:</strong>
                            <pre style={{ 
                              fontSize: '0.8em', 
                              backgroundColor: '#e8f5e8', 
                              padding: '8px', 
                              borderRadius: '4px',
                              marginTop: '5px',
                              overflow: 'auto',
                              maxHeight: '150px'
                            }}>
                              {chat.tool_output}
                            </pre>
                          </div>
                        )}
                      </details>
                    )}
                  </div>
                </div>
              ) : chat.type === 'bot' && (chat.message || chat.graph || chat.file) ? (
                <div className="assistant-message-container">
                  <img src={assistantIcon} alt="Assistant Icon" className="assistant-icon" />
                  <div className="assistant-text">
                    {chat.graph ? (
                      <Plot data={chat.graph.data} layout={chat.graph.layout} />
                    ) : chat.file ? (
                      <div className="file-display">
                        {chat.file.type === 'image' ? (
                          <div>
                            <img
                              src={`${BACKEND_BASE}/static/${chat.file.name}`}
                              alt={chat.file.name}
                              style={{ maxWidth: '100%', height: 'auto', margin: '10px 0' }}
                            />
                            <p style={{ fontSize: '0.9em', color: '#666', margin: '5px 0' }}>
                              📊 {chat.file.name}
                            </p>
                          </div>
                        ) : chat.file.type === 'rtf' ? (
                          <div>
                            <a
                              href={`${BACKEND_BASE}/static/${chat.file.name}`}
                              download={chat.file.name}
                              style={{
                                display: 'inline-block',
                                padding: '10px 20px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                textDecoration: 'none',
                                borderRadius: '5px',
                                margin: '10px 0'
                              }}
                            >
                              📥 Download RTF: {chat.file.name}
                            </a>
                          </div>
                        ) : (
                          <div>
                            <a
                              href={`${BACKEND_BASE}/static/${chat.file.name}`}
                              download={chat.file.name}
                              style={{
                                display: 'inline-block',
                                padding: '10px 20px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                textDecoration: 'none',
                                borderRadius: '5px',
                                margin: '10px 0'
                              }}
                            >
                              📎 Download: {chat.file.name}
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      renderMessageContent(chat.message)
                    )}
                  </div>
                </div>
              ) : chat.file ? (
                <div className="file-upload-bubble">
                  <FaFile className="file-icon" />
                  <p className="file-name">{chat.file.name}</p>
                </div>
              ) : (
                <div className="chat-bubble user">
                  <p>{chat.message}</p>
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="chat-bubble bot">
              <div className="loading-indicator">
                <span className="dot">•</span>
                <span className="dot">•</span>
                <span className="dot">•</span>
              </div>
            </div>
          )}
        </div>

        <div className="suggestion-bubbles">
          {suggestionBubbles.map((bubble, index) => (
            <button
              key={index}
              className={`suggestion-bubble ${loading ? 'disabled' : ''}`}
              onClick={() => handleSuggestionClick(bubble.text)}
              disabled={loading}
            >
              {bubble.text}
            </button>
          ))}
        </div>

        <div className="input-area">
          <div className="chat-input-container">
            <button className="attachment-button">
              <label htmlFor="file-upload">
                <CgAttachment size={18} />
              </label>
              <input
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </button>
            <textarea
              value={input}
              onChange={handleInputChange}
              placeholder="Ask me anything..."
              rows="1"
              className="chat-input"
            />
            <button 
              onClick={handleSubmit} 
              className={`send-button ${loading ? 'disabled' : ''}`}
              disabled={loading}
            >
              <MdSend size={20} />
            </button>
            <button
              className={`mic-button ${isRecording ? 'recording' : ''} ${loading ? 'disabled' : ''}`}
              onClick={handleMicClick}
              disabled={loading}
            >
              <IoMicOutline size={25} />
            </button>
          </div>
        </div>
      </div>

      {showGuidedRun && (
        <GuidedRun 
          onClose={handleGuidedRunClose}
          onSendMessage={handleGuidedRunMessage}
        />
      )}
    </div>
  );
}

export default App;
